/**
 * Google Tag Manager, the same dataLayer approach budget-calculator uses.
 *
 * Unlike that app, this one is never embedded in someone else's page, so there
 * is no postMessage bridge and no embedding mode to detect: this origin always
 * owns its own container, or analytics is off.
 */
type DataLayerObject = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: DataLayerObject[];
  }
}

interface GtmConfig {
  id: string;
  enabled?: boolean;
}

export type AnalyticsParams = Record<string, string | number | boolean | null>;

let config: GtmConfig | null = null;
let scriptLoaded = false;

export function initGtm(opts: GtmConfig): void {
  config = opts;
  if (!opts.id || opts.enabled === false) return;
  window.dataLayer = window.dataLayer ?? [];
  if (scriptLoaded) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${opts.id}`;
  const first = document.getElementsByTagName('script')[0];
  first?.parentNode?.insertBefore(script, first);
  scriptLoaded = true;
}

function pushEvent(event: DataLayerObject): void {
  if (!config?.id || config.enabled === false) return;
  window.dataLayer?.push(event);
}

/**
 * The container whitelists event names and parameter keys and silently drops
 * anything it does not recognise, so a new event has to be added there and
 * published first or it is lost without any error.
 *
 * Never pass personally identifying values (email, phone, name) in `params` —
 * they would land in GA4, which is both a policy breach and against Google's
 * terms. The quote option uuid counts: it is a share token for someone's
 * quotation, so it stays out of analytics.
 */
export function trackEvent(event: string, params: AnalyticsParams = {}): void {
  pushEvent({ event, ...params });
}

export function trackPageView(pageName: string, params: AnalyticsParams = {}): void {
  trackEvent('page', { pageName, ...params });
}
